import React, { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useAppDispatch, useAppSelector, WishlistPathValue } from '@pkg/hooks';

import { globalNavigationActions } from '@app/store/slices';

import { NavButton } from '../../NavButton';
import { Toggle } from '../../Toggle';
import { InlineButton } from '../../Toggle/styles';
import { collapseTransition, groupAnimation, itemAnimation } from '../../animations';
import { Buttons, Collapse, Collapseable } from '../../styles';

interface SectionProps {
    isOpen: boolean;
    setIsOpen?: (isOpen: boolean) => void;
}

export const WishlistsSection = ({ isOpen, setIsOpen }: SectionProps) => {
    const location = useLocation();

    useEffect(() => {
        if (location.pathname.startsWith('/wishlists') && setIsOpen) {
            setIsOpen(true);
        }
    }, [location.pathname, setIsOpen]);

    const showCreateWishlistModal = useAppSelector((state) => state.globalNavigation.showCreateWishlistModal);
    const dispatch = useAppDispatch();

    const { t: translate } = useTranslation(['navigation']);

    const handleCreateWishlistToggle = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(globalNavigationActions.showCreateWishlistModal(!showCreateWishlistModal));
    };

    const slugPath = location.pathname.replace('/wishlists/', '');

    return (
        <Collapseable>
            <Toggle
                emoji="⭐"
                count={0}
                showWhenOpen={<InlineButton onClick={handleCreateWishlistToggle}>New +</InlineButton>}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                aria-expanded={isOpen}
                aria-controls="sidenav-section-wishlists"
            >
                Wishlists
            </Toggle>

            <Collapse
                variants={groupAnimation}
                initial={false}
                animate={isOpen ? 'open' : 'closed'}
                transition={collapseTransition}
                aria-labelledby="sidenav-section-wishlists"
            >
                <Buttons>
                    <NavButton
                        to={`/wishlists/${WishlistPathValue.popular}`}
                        variants={itemAnimation}
                        isActive={location.pathname === '/wishlists' || slugPath === WishlistPathValue.popular}
                    >
                        {translate('wishlist_tabs_popular')}
                    </NavButton>
                    <NavButton
                        to={`/wishlists/${WishlistPathValue.following}`}
                        count={0}
                        variants={itemAnimation}
                        isActive={slugPath === WishlistPathValue.following}
                    >
                        {translate('wishlist_tabs_following')}
                    </NavButton>
                    <NavButton
                        to={`/wishlists/${WishlistPathValue.myWishlist}`}
                        count={0}
                        variants={itemAnimation}
                        isActive={slugPath === WishlistPathValue.myWishlist}
                    >
                        {translate('wishlist_tabs_my_wishlists')}
                    </NavButton>
                </Buttons>
            </Collapse>
        </Collapseable>
    );
};
